import React from 'react';
import { Title } from 'Components/frameworks/title/Title';
import type { FormProduct } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { useFormikContext } from 'formik';
import { t } from 'Src/i18n';
import { momentFromUTCDateTime } from 'Utils/date/dateUtils';
import { CriteriaElement } from './CriteriaElement';
import { FailureEstimationNotice } from './FailureEstimationNotice';
import { PriceEstimationCostProposal } from './PriceEstimationCostProposal';
const FETCH_PROPOSAL_ERRORS = {
  noTransport: 'noTransport',
  notOnSaleForThePeriod: 'notOnSaleForThePeriod',
  noAccommodation: 'noAccommodation',
  noPrice: 'noPrice',
};

const getCouncelDirectiveByError = (error: string | null | undefined) => {
  switch (error) {
    case FETCH_PROPOSAL_ERRORS.noTransport:
      return t('prebookingForm.estimativePrice.noTransportAvailable');

    case FETCH_PROPOSAL_ERRORS.notOnSaleForThePeriod:
      return t('prebookingForm.estimativePrice.noDatesAvailable');

    case FETCH_PROPOSAL_ERRORS.noAccommodation:
      return t('prebookingForm.estimativePrice.noRoomAvailable');

    case FETCH_PROPOSAL_ERRORS.noPrice:
      return t('prebookingForm.estimativePrice.noPriceAvailable');

    default:
      return '';
  }
};

export const EstimationCostProposal = ({
  proposal,
  product,
  fetchProposalError,
  departureCities = [],
}: {
  proposal: Proposal | null | undefined;
  product: FormProduct | null | undefined;
  fetchProposalError: string | null | undefined;
  departureCities?: Array<{
    value: string;
    name: string;
  }>;
}) => {
  const { values } = useFormikContext();
  const accommodationCategoryLabel = product?.accommodations?.find(
    ({ value }) => value === values?.accommodationCategory,
  )?.name;
  const departureCityLabel =
    values?.departureCity === 'NO'
      ? t('prebookingForm.estimativePrice.noTransportation')
      : departureCities.find(({ value }) => value === values?.departureCity)?.name || '';
  return (
    <div id="pbf-step-1">
      <Title className="mb-5 mt-30 px-10" hLevel={3} theme="title-sans-n4" id="pbf-step-1-title">
        2. {t('prebookingForm.estimativePrice.title')}
      </Title>
      <div>
        {proposal?.id && (
          <div className="flex gap-5">
            <h3>{t('prebookingForm.estimativePrice.proposalNumber')}</h3>
            <span className="font-bold"> {proposal?.id || ''}</span>
          </div>
        )}
        <div>
          {/* Destination */}
          <CriteriaElement
            iconName={ICONS.EARTH}
            criteriaLabel={t('prebookingForm.stay.productLabel')}
            criteriaValue={`${product?.name || ''}, ${product?.destination?.country || ''}`}
            isValidCriteria
          />
          {/* Transport */}
          <CriteriaElement
            iconName={ICONS.PLANE_V2}
            criteriaLabel={t('prebookingForm.stay.departureCityLabel')}
            criteriaValue={departureCityLabel}
            isValidCriteria={fetchProposalError !== FETCH_PROPOSAL_ERRORS.noTransport}
          />
          {/* Stay duration */}
          <CriteriaElement
            iconName={ICONS.CALENDAR_EMPTY}
            criteriaLabel={t('prebookingForm.stay.durationLabel')}
            criteriaValue={`${momentFromUTCDateTime(values?.departureDate?.from).format('DD/MM/YYYY') || ''} (${t(
              'prebookingForm.stay.durationValue',
              {
                count: values?.duration,
              },
            )})`}
            isValidCriteria={fetchProposalError !== FETCH_PROPOSAL_ERRORS.notOnSaleForThePeriod}
          />
          {/* Accommodation (comfort) */}
          <CriteriaElement
            iconName={ICONS.BED}
            criteriaLabel={t('prebookingForm.stay.accommodationsLabel')}
            criteriaValue={accommodationCategoryLabel}
            isValidCriteria={fetchProposalError !== FETCH_PROPOSAL_ERRORS.noAccommodation}
          />
          {/* participants */}
          <CriteriaElement
            iconName={ICONS.PEOPLE_DOUBLE}
            criteriaLabel={t('prebookingForm.stay.numberOfParticipantsLabel')}
            criteriaValue={`${t('prebookingForm.stay.adultsAge', {
              count: Number(values?.adultsNumber) || 0,
            })} - ${t('prebookingForm.stay.childrenAge', {
              count: Number(values?.kidsNumber) || 0,
            })}`}
          />
          {/* proposal price estimation */}
          {proposal?.price ? (
            <PriceEstimationCostProposal
              productId={product?.productId}
              total={proposal?.price?.total?.formattedValue}
              totalWithoutDiscount={proposal?.price?.totalWithoutDiscount?.formattedValue}
            />
          ) : (
            <FailureEstimationNotice
              problemError={t('prebookingForm.estimativePrice.notAvailable')}
              councelDirective={getCouncelDirectiveByError(fetchProposalError)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
